import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import TextArea from '@/common/components/TextArea'
import TextField from '@/common/components/TextField'
import { UnitTextField } from '@/common/components/UnitTextField'
import { useDriverLicenseCategories } from '@/common/hooks/queries/useDriverLicenseCategories'
import { useEquipmentTiers } from '@/common/hooks/queries/useEquipmentTiers'
import useDebounce from '@/common/hooks/useDebounce'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { Mapper } from '@/common/services/mapper'
import { DriverLicenseCategoryData } from '@/common/types/driverLicenseCategory'
import { EquipmentTierData } from '@/common/types/equipmentTier'
import { formatDecimal, formatUnit } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import { requiredFieldStrategy } from '@/features/equipmentCategories/constants/requiredFieldOptions.constant'
import { unitForConsumptionStrategy } from '@/features/equipmentCategories/constants/unitForConsumptionOptions.constants'
import { useEquipmentCategory } from '@/features/equipmentCategories/hooks/useEquipmentCategory'
import { useUpdateEquipmentCategory } from '@/features/equipmentCategories/hooks/useUpdateEquipmentCategory'
import {
  equipmentCategorySchema,
  EquipmentCategoryValidationSchema,
} from '@/features/equipmentCategories/schemas/equipmentCategory.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { BoxesIcon, ChevronsRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'

const EquipmentCategoriesUpdate = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchEquipmentTier, setSearchEquipmentTier] = useDebounce('')
  const [searchDriverLicenseCategories, setSearchDriverLicenseCategories] = useDebounce('');

  const { data: equipmentCategory, isLoading: equipmentCategoryIsLoading } = useEquipmentCategory(id || '')

  const { data: equipmentTierOptions } = useEquipmentTiers({
    filters: { search: searchEquipmentTier },
    mapper: Mapper.mapToOptions<EquipmentTierData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  })

  const { data: driverLicenseCategoriesOptions } = useDriverLicenseCategories({
    filters: { search: searchDriverLicenseCategories },
    mapper: Mapper.mapToOptions<DriverLicenseCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' })
  })

  const {
    mutate: updateEquipmentCategory,
    isPending: updateEquipmentCategoryIsPending,
    isError: updateEquipmentCategoryIsError,
  } = useUpdateEquipmentCategory({
    equipmentCategoryId: id || '',
    initialEquipmentCategory: equipmentCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipmentCategories'] })
      queryClient.invalidateQueries({ queryKey: ['equipmentCategory', id] })
      navigate(`/equipment-categories/${id}`)
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error)
    },
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasUpdateEquipmentCategoryPermission = !isUserPermissionsPending ? hasUserPermission('Admin::EquipmentCategories::Update') : true

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<EquipmentCategoryValidationSchema>({
    resolver: zodResolver(equipmentCategorySchema),
  })

  const onSubmit: SubmitHandler<EquipmentCategoryValidationSchema> = (data) => {
    updateEquipmentCategory(data)
  }

  useEffect(() => {
    if (!equipmentCategoryIsLoading && equipmentCategory) {
      reset({
        display_name: equipmentCategory.display_name,
        description: equipmentCategory.description,
        equipment_tier: equipmentCategory.equipment_tier
          ? {
              label: equipmentCategory.equipment_tier.display_name,
              value: equipmentCategory.equipment_tier.id,
            }
          : null,
        bypass_consumption_usage_validations: equipmentCategory.bypass_consumption_usage_validations,
        max_tank_capacity: `${formatUnit(formatDecimal(equipmentCategory?.max_tank_capacity || '', 0), 'liter')}`,
        driver_license_category_equipment_categories: equipmentCategory.driver_license_category_equipment_categories.map((item) => ({
          value: item.driver_license_category.id,
          label: item.driver_license_category.display_name
        })),
      })
    }
  }, [equipmentCategoryIsLoading])

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasUpdateEquipmentCategoryPermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/equipment-categories/${id}`} searchLastRouteFromFallback />
            <p className="text-white-400 font-normal">Categorias de equipamento</p>
            <ChevronsRight color="#ffffffb3" />
            <h2>{equipmentCategory?.display_name}</h2>
          </div>

          <div className="flex gap-4">
            <Link to={`/equipment-categories/${id}`}>
              <Button type="button" variant="outline-error">
                Cancelar
              </Button>
            </Link>

            <Button type="submit" data-testid="update_equipment_categories">
              Confirmar
            </Button>
          </div>
        </div>

        {(equipmentCategoryIsLoading || isUserPermissionsPending) ? (
          <Box>Carregando...</Box>
        ) : (
          <Box className="flex max-h-[calc(100vh-128px)] flex-col gap-y-4 overflow-y-auto">
            <p className="text-lg font-bold">Categorias de equipamento</p>

            <TextField
              {...register('display_name')}
              label="Nome"
              maxLength={64}
              errorMessage={errors?.display_name?.message}
              disabled={updateEquipmentCategoryIsPending}
            />

            <TextArea
              {...register('description')}
              label="Descrição"
              maxLength={256}
              errorMessage={errors?.description?.message}
              disabled={updateEquipmentCategoryIsPending}
            />

            <SelectController
              control={control}
              name="equipment_tier"
              label="Porte"
              options={equipmentTierOptions || []}
              onInputChange={setSearchEquipmentTier}
              isClearable
              errorMessage={errors?.equipment_tier?.message}
              isDisabled={updateEquipmentCategoryIsPending}
            />

            <Box className="flex flex-col gap-y-2 rounded-lg p-4">
              <p className="text-[0.625rem] text-white-800">Campos obrigatórios para cadastro de equipamento</p>

              <div className="flex flex-wrap gap-2">
                {equipmentCategory?.required_field_names?.map((field) => (
                  <div key={field} className="flex gap-x-1 items-center bg-white-200 rounded-sm p-2">
                    <BoxesIcon className="size-6 text-white-800" strokeWidth={1} />

                    <p className="text-xs font-bold text-white-800">
                      {requiredFieldStrategy[field as keyof typeof requiredFieldStrategy]}
                    </p>
                  </div>
                ))}
              </div>
            </Box>

            <SelectController
              label="Categorias de CNH aceitas"
              name="driver_license_category_equipment_categories"
              control={control}
              options={driverLicenseCategoriesOptions}
              isMulti
              onInputChange={value => setSearchDriverLicenseCategories(value)}
              errorMessage={errors?.driver_license_category_equipment_categories?.message as string}
            />

            <div className="flex gap-x-3 w-full">
              <Box className="flex flex-col w-full gap-y-2 rounded-lg p-4">
                <p className="text-[0.625rem] text-white-800">Tipo de identificação</p>

                <p className="text-xs text-white-800">
                  {requiredFieldStrategy[equipmentCategory?.field_for_display as keyof typeof requiredFieldStrategy]}
                </p>
              </Box>

              <Box className="flex flex-col w-full gap-y-2 rounded-lg p-4">
                <p className="text-[0.625rem] text-white-800">Métrica da categoria</p>

                <p className="text-xs text-white-800">
                  {
                    unitForConsumptionStrategy[
                    equipmentCategory?.unit_for_consumption as keyof typeof unitForConsumptionStrategy
                    ]
                  }
                </p>
              </Box>
            </div>

            <Controller
              name="max_tank_capacity"
              control={control}
              disabled={updateEquipmentCategoryIsPending}
              render={({ field }) => (
                <UnitTextField
                  unit="L"
                  maxValue={99_999}
                  decimals={0}
                  label="Capacidade máxima do tanque"
                  errorMessage={errors?.max_tank_capacity?.message}
                  {...field}
                />
              )}
            />

            <Checkbox
              label="É uma categoria presente na FIPE"
              className='italic text-white-400'
              disabled
            />

            <Checkbox
              label="É uma categoria que suporta INFOCAR"
              checked={equipmentCategory?.supports_infocar_data_enrichment}
              className='italic text-white-400'
              disabled
            />

            <Checkbox
              label="É uma categoria que não precisa de controle por quilometragem/hora uso"
              defaultChecked={equipmentCategory?.bypass_consumption_usage_validations}
              {...register('bypass_consumption_usage_validations')}
              disabled={updateEquipmentCategoryIsPending}
            />
          </Box>
        )}
      </form>
      {messages && (
        <ModalMessageSimple messages={messages} setMessages={setMessages} isError={updateEquipmentCategoryIsError} />
      )}
    </ErrorMessageWrapper>
  )
}

export default EquipmentCategoriesUpdate
